import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Sidebar from "./Sidebar";
import "../Admin.css";
import "./CategoryManager.css";

const API_URL = `${process.env.REACT_APP_API_URL}/categorias`;

const CategoryManager = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingCategory, setEditingCategory] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    // Form States
    const [catLabel, setCatLabel] = useState("");
    const [subInput, setSubInput] = useState("");
    const [subcategories, setSubcategories] = useState([]);

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const response = await axios.get(API_URL);
            setCategories(response.data);
        } catch (error) {
            console.error("Erro ao buscar categorias:", error);
        } finally {
            setLoading(false);
        }
    };

    const filteredCategories = useMemo(() => {
        if (!searchTerm) return categories;
        const term = searchTerm.toLowerCase();
        return categories.filter(cat => 
            cat.name.toLowerCase().includes(term) || 
            (cat.subcategories && cat.subcategories.some(sub => sub.name.toLowerCase().includes(term)))
        );
    }, [categories, searchTerm]);

    const handleAddSubcategory = (e) => {
        if ((e.key === 'Enter' || e.type === 'click') && subInput.trim()) {
            e.preventDefault();
            const newSub = {
                id: subInput.trim().toLowerCase().replace(/\s+/g, '_'),
                name: subInput.trim(),
                slug: subInput.trim().toLowerCase().replace(/\s+/g, '-')
            };
            if (!subcategories.find(s => s.id === newSub.id)) {
                setSubcategories([...subcategories, newSub]);
            }
            setSubInput("");
        }
    };

    const handleRemoveSubcategory = (id) => {
        setSubcategories(subcategories.filter(s => s.id !== id));
    };

    const resetForm = () => {
        setEditingCategory(null);
        setCatLabel("");
        setSubcategories([]);
        setSubInput("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const categoryData = {
            id: editingCategory ? editingCategory.id : catLabel.toLowerCase().replace(/\s+/g, '_'),
            name: catLabel,
            slug: catLabel.toLowerCase().replace(/\s+/g, '-'),
            subcategories: subcategories
        };

        try {
            if (editingCategory) {
                await axios.put(`${API_URL}/${editingCategory.id}`, categoryData);
            } else {
                await axios.post(API_URL, categoryData);
            }
            fetchCategories();
            resetForm();
        } catch (error) {
            console.error("Erro ao salvar categoria:", error);
        }
    };

    const handleEdit = (category) => {
        setEditingCategory(category);
        setCatLabel(category.name);
        setSubcategories(category.subcategories || []);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Tem certeza que deseja excluir esta categoria?")) {
            try {
                await axios.delete(`${API_URL}/${id}`);
                fetchCategories();
            } catch (error) {
                console.error("Erro ao excluir categoria:", error);
            }
        }
    };

    return (
        <div className="admin-layout">
            <Sidebar />
            <div className="admin-content category-manager-container">
                <div className="category-header">
                    <div>
                        <h2><i className="fas fa-tags"></i> Gerenciar Categorias</h2>
                        <p style={{ color: 'var(--text-secondary)', marginTop: '5px' }}>
                            Configure as categorias e subcategorias para os produtos
                        </p>
                    </div>
                </div>

                {/* Form Section */}
                <div className="card-container">
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label>Nome da Categoria</label>
                            <input
                                type="text"
                                value={catLabel}
                                onChange={(e) => setCatLabel(e.target.value)}
                                placeholder="Ex: Eletrônicos"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Subcategorias (Pressione Enter para adicionar)</label>
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                <input
                                    type="text"
                                    value={subInput}
                                    onChange={(e) => setSubInput(e.target.value)}
                                    onKeyDown={handleAddSubcategory}
                                    placeholder="Ex: Smartphones"
                                />
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={handleAddSubcategory}
                                    style={{ padding: '0 20px' }}
                                >
                                    Add
                                </button>
                            </div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                                {subcategories.length === 0 && <span style={{ color: '#999', fontSize: '0.9rem' }}>Nenhuma subcategoria adicionada.</span>}
                                {subcategories.map(sub => (
                                    <span key={sub.id} className="subcategory-tag" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {sub.name}
                                        <i
                                            className="fas fa-times"
                                            style={{ cursor: 'pointer', color: '#dc3545', fontSize: '0.8rem' }}
                                            onClick={() => handleRemoveSubcategory(sub.id)}
                                        ></i>
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="form-actions" style={{ justifyContent: 'flex-end', marginTop: '30px' }}>
                            {editingCategory && (
                                <button type="button" className="btn-secondary" onClick={resetForm} style={{ backgroundColor: '#6c757d' }}>
                                    Cancelar
                                </button>
                            )}
                            <button type="submit" className="btn-primary" style={{ padding: '12px 30px' }}>
                                {editingCategory ? "Salvar Alterações" : "Criar Categoria"}
                            </button>
                        </div>
                    </form>
                </div>

                {/* List Section */}
                <div className="category-card">
                    <div className="category-card-header">
                        <h3>Categorias Existentes</h3>
                        <div className="search-box">
                            <i className="fas fa-search"></i>
                            <input 
                                type="text" 
                                placeholder="Buscar categoria ou subcategoria..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    
                    <div className="category-table-container">
                        {loading ? (
                            <div className="loading">
                                <i className="fas fa-spinner fa-spin"></i> Carregando dados...
                            </div>
                        ) : (
                            <table>
                                <thead>
                                    <tr>
                                        <th style={{ width: '30%' }}>Categoria</th>
                                        <th>Subcategorias</th>
                                        <th style={{ width: '200px' }}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCategories.map((cat) => (
                                        <tr key={cat.id}>
                                            <td>
                                                <div style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>{cat.name}</div>
                                                <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '4px' }}>CODE: {cat.id}</div>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                    {cat.subcategories && cat.subcategories.length > 0 ? (
                                                        cat.subcategories.map(s => (
                                                            <span key={s.id} className="subcategory-tag">
                                                                {s.name}
                                                            </span>
                                                        ))
                                                    ) : (
                                                        <span style={{ color: '#ccc', fontStyle: 'italic', fontSize: '0.85rem' }}>Nenhuma subcategoria</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                    <button
                                                        className="btn-category-action btn-secondary"
                                                        onClick={() => handleEdit(cat)}
                                                        style={{ padding: '6px 12px' }}
                                                    >
                                                        <i className="fas fa-edit"></i> Editar
                                                    </button>
                                                    <button
                                                        className="btn-category-action btn-danger"
                                                        onClick={() => handleDelete(cat.id)}
                                                        style={{ padding: '6px 12px' }}
                                                    >
                                                        <i className="fas fa-trash"></i> Excluir
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredCategories.length === 0 && (
                                        <tr>
                                            <td colSpan="3" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                                <i className="fas fa-search" style={{ fontSize: '2rem', opacity: 0.1, display: 'block', marginBottom: '10px' }}></i>
                                                {searchTerm ? `Nenhum resultado para "${searchTerm}"` : "Nenhuma categoria cadastrada."}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CategoryManager;
